// @ts-nocheck
// ═══ BUILDER RENDER ═══════════════════════════════════════
// Canvas + palette rendering ported verbatim from legacy/EMR Forms.html:
// the left "Fields" palette, the question-card renderer (drag-reorder,
// collapse/expand, multi-select copy/paste), the per-type config panels
// (buildCfg + options/grid/scale editors), the skip-logic "go to" dropdown
// machinery (buildTargetOpts / sectionUsageMap / populateGoto / rebuildGotos),
// the branding-logo picker, and the cover-media upload handling.
//
// Every DOM lookup is scoped to ctx.container (the legacy used document.* on a
// single-page document). Cross-module calls (syncJson, renderSections) go
// through ctx slots that mount.ts fills before any interaction fires.

import type { Ctx } from './engineTypes.ts';
import { esc } from './state.ts';
import { TC, HC, HG, HS, HCN, HF, initMeta } from './typeConfig.ts';
import { COUNTRIES } from './countries.ts';

export function attachBuilderRender(ctx: Ctx): void {
  const doc = ctx.container;
  const state = ctx.state;
  const toast = ctx.toast;
  const $ = (id: string): any => doc.querySelector('#' + id);
  const $$ = (sel: string): any => doc.querySelectorAll(sel);
  const syncJson = () => ctx.syncJson();
  const renderSections = () => ctx.renderSections();
  const uid = () => ctx.uid();

  const logoDataUrl = (key: string) => (state.logos && (state.logos as any)[key]) || '';

  // ═══ PALETTE ══════════════════════════════════════════════
  function renderPalette() {
    const root = $('palette-root'); if (!root) return; root.innerHTML = '';
    const cats: Record<string, any[]> = {};
    Object.entries(TC).forEach(([type, cfg]: any) => { (cats[cfg.cat] = cats[cfg.cat] || []).push({ type, cfg }); });
    Object.entries(cats).forEach(([cat, items]: any) => {
      const wrap = document.createElement('div'); wrap.className = 'pal-cat';
      const title = document.createElement('div'); title.className = 'pal-cat-title'; title.textContent = cat;
      const grid = document.createElement('div'); grid.className = 'pal-grid';
      items.forEach(({ type, cfg }: any) => {
        const item = document.createElement('div'); item.className = 'pal-item'; item.draggable = true; item.dataset.type = type; item.title = cfg.l;
        item.innerHTML = `<div class="pal-icon" style="background:${cfg.bg};color:${cfg.c}">${cfg.i}</div><div class="pal-label">${cfg.l}</div>`;
        item.addEventListener('click', () => addQ(type));
        item.addEventListener('dragstart', (e: any) => e.dataTransfer.setData('type', type));
        grid.appendChild(item);
      });
      wrap.append(title, grid); root.appendChild(wrap);
    });
  }

  // ═══ LOGO BRANDING (two user-uploaded slots) ══════════════
  let _uploadSlot = 'a';
  function renderLogoPicker() {
    const root = $('logo-opts'); if (!root) return; root.innerHTML = '';
    ['a', 'b'].forEach((key, i) => {
      const url = (state.logos as any)[key];
      if (url) {
        const opt = document.createElement('div');
        opt.className = 'logo-opt' + (state.logo === key ? ' selected' : '');
        opt.title = 'Use this logo for branding';
        const img = document.createElement('img'); img.src = url; img.alt = 'Logo ' + (i + 1);
        opt.appendChild(img);
        opt.onclick = () => { state.logo = key; renderLogoPicker(); syncJson(); };
        const tools = document.createElement('div'); tools.className = 'logo-tools';
        const rep = document.createElement('button'); rep.type = 'button'; rep.className = 'logo-tool'; rep.textContent = 'Replace'; rep.title = 'Replace this logo';
        rep.onclick = (e: any) => { e.stopPropagation(); _uploadSlot = key; $('logo-file').click(); };
        const rem = document.createElement('button'); rem.type = 'button'; rem.className = 'logo-tool del'; rem.textContent = '✕'; rem.title = 'Remove this logo';
        rem.onclick = (e: any) => { e.stopPropagation(); removeLogo(key); };
        tools.append(rep, rem); opt.appendChild(tools);
        root.appendChild(opt);
      } else {
        const up = document.createElement('button'); up.type = 'button'; up.className = 'logo-opt logo-upload';
        up.innerHTML = '<span>⬆ Upload logo ' + (i + 1) + '</span>';
        up.onclick = () => { _uploadSlot = key; $('logo-file').click(); };
        root.appendChild(up);
      }
    });
  }
  function removeLogo(key: string) {
    (state.logos as any)[key] = null;
    if (state.logo === key) { const other = key === 'a' ? 'b' : 'a'; state.logo = (state.logos as any)[other] ? other : ''; }
    renderLogoPicker(); syncJson(); toast('Logo removed.', 'info');
  }
  (function wireLogoUpload() {
    const fi = $('logo-file'); if (!fi) return;
    fi.addEventListener('change', () => {
      const f = fi.files[0]; if (!f) return;
      if (!f.type.startsWith('image/')) { toast('Please choose an image file for the logo.', 'err'); fi.value = ''; return; }
      if (f.size > 1024 * 1024) { toast('Logo image must be under 1 MB.', 'err'); fi.value = ''; return; }
      const reader = new FileReader();
      reader.onload = () => { (state.logos as any)[_uploadSlot] = reader.result; state.logo = _uploadSlot; renderLogoPicker(); syncJson(); toast('Logo uploaded.', 'ok'); };
      reader.readAsDataURL(f); fi.value = '';
    });
  })();

  // ═══ COVER MEDIA ══════════════════════════════════════════
  const coverArea = $('cover-area'), coverUpload = $('cover-upload');
  const coverImgPrev = $('cover-img-preview'), coverVidPrev = $('cover-vid-preview');
  const coverPlaceholder = $('cover-placeholder'), coverRemoveBtn = $('cover-remove');
  coverArea.addEventListener('click', (e: any) => { if (e.target === coverRemoveBtn) return; if (!coverArea.classList.contains('has-media')) coverUpload.click(); });
  coverUpload.addEventListener('change', () => {
    const file = coverUpload.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev: any) => {
      const isImg = file.type.startsWith('image/');
      state.coverMedia = { type: isImg ? 'image' : 'video', dataUrl: ev.target.result };
      coverImgPrev.classList.toggle('hide', !isImg); coverVidPrev.classList.toggle('hide', isImg);
      if (isImg) coverImgPrev.src = ev.target.result; else coverVidPrev.src = ev.target.result;
      coverArea.classList.add('has-media'); coverPlaceholder.classList.add('hide'); coverRemoveBtn.classList.remove('hide');
      syncJson();
    };
    reader.readAsDataURL(file);
  });
  coverRemoveBtn.addEventListener('click', (e: any) => {
    e.stopPropagation(); state.coverMedia = null;
    coverImgPrev.src = ''; coverVidPrev.src = '';
    [coverImgPrev, coverVidPrev, coverRemoveBtn].forEach((el: any) => el.classList.add('hide'));
    coverArea.classList.remove('has-media'); coverPlaceholder.classList.remove('hide');
    coverUpload.value = ''; syncJson();
  });
  function restoreCoverMedia(cm: any) {
    if (!cm) return;
    state.coverMedia = cm;
    const isImg = cm.type === 'image';
    coverImgPrev.classList.toggle('hide', !isImg);
    coverVidPrev.classList.toggle('hide', isImg);
    if (isImg) coverImgPrev.src = cm.dataUrl;
    else coverVidPrev.src = cm.dataUrl;
    coverArea.classList.add('has-media');
    coverPlaceholder.classList.add('hide');
    coverRemoveBtn.classList.remove('hide');
  }

  // ═══ DRAG-DROP ZONE ═══════════════════════════════════════
  const drop = $('dropzone');
  drop.addEventListener('dragover', (e: any) => { e.preventDefault(); drop.classList.add('drag-over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('drag-over'));
  drop.addEventListener('drop', (e: any) => { e.preventDefault(); drop.classList.remove('drag-over'); const t = e.dataTransfer.getData('type'); if (t) addQ(t); });

  // ═══ HELPERS ══════════════════════════════════════════════
  let _selectedQids = new Set<string>();
  let _anchorQid: string | null = null;
  let _clipboardQs: any[] = [];
  const _collapsedQids = new Set<string>();
  function _updateCollapseAllBtn() {
    const btn = $('collapse-all');
    if (!btn) return;
    if (!state.questions.length) { btn.classList.add('hide'); return; }
    btn.classList.remove('hide');
    const allCollapsed = state.questions.every((q: any) => _collapsedQids.has(q.id));
    btn.innerHTML = allCollapsed ? '⊞ Expand all' : '⊟ Collapse all';
    btn.title = allCollapsed ? 'Expand every question card' : 'Collapse every question card to its header';
  }
  function updateBadge() { $('q-count-badge').textContent = state.questions.length + ' question' + (state.questions.length !== 1 ? 's' : ''); }
  const dzEmpty = $('dz-empty');
  function toggleEmpty() { dzEmpty.style.display = state.questions.length === 0 ? 'flex' : 'none'; }

  // ═══ ADD / RENDER QUESTIONS ════════════════════════════════
  function addQ(type: string) {
    const id = uid(), q = { id, type, label: TC[type]?.l || type, required: false, meta: initMeta(type) };
    state.questions.push(q); renderQuestions(); syncJson(); rebuildGotos(); renderSections();
  }
  function renderQuestions() {
    drop.innerHTML = ''; drop.appendChild(dzEmpty);
    toggleEmpty(); updateBadge();
    state.questions.forEach((q: any, i: number) => { if (q.order == null) q.order = i; });
    state.questions.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    state.questions.forEach((q: any, idx: number) => {
      const card = document.createElement('div'); card.className = 'q-card'; card.dataset.qid = q.id; card.draggable = true;
      if (_selectedQids.has(q.id)) card.classList.add('selected');
      if (_collapsedQids.has(q.id)) card.classList.add('collapsed');
      card.addEventListener('click', (e: any) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        if (e.target.closest('input,textarea,select,button,label')) return;
        e.preventDefault(); e.stopPropagation();
        if (_selectedQids.has(q.id)) _selectedQids.delete(q.id);
        else _selectedQids.add(q.id);
        _anchorQid = q.id;
        card.classList.toggle('selected', _selectedQids.has(q.id));
      }, true);
      card.addEventListener('dragstart', (e: any) => { e.dataTransfer.setData('idx', String(idx)); setTimeout(() => card.classList.add('dragging'), 0); });
      card.addEventListener('dragend', () => card.classList.remove('dragging'));
      card.addEventListener('dragover', (e: any) => e.preventDefault());
      card.addEventListener('drop', (e: any) => {
        e.preventDefault(); const from = +e.dataTransfer.getData('idx');
        if (!isNaN(from) && from !== idx) { const [mv] = state.questions.splice(from, 1); state.questions.splice(idx, 0, mv); state.questions.forEach((qq: any, i: number) => qq.order = i); renderQuestions(); syncJson(); rebuildGotos(); renderSections(); }
      });
      const inner = document.createElement('div'); inner.className = 'q-card-inner';
      const hdr = document.createElement('div'); hdr.className = 'q-header';
      const drag = document.createElement('div'); drag.className = 'q-drag'; drag.title = 'Drag to reorder';
      drag.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>';
      const num = document.createElement('div'); num.className = 'q-num'; num.textContent = String(idx + 1);
      const labelInp = document.createElement('input'); labelInp.type = 'text'; labelInp.className = 'q-label-input'; labelInp.value = q.label;
      labelInp.addEventListener('input', () => { q.label = labelInp.value; syncJson(); });
      const badge = document.createElement('span'); badge.className = 'q-type-badge'; badge.textContent = TC[q.type]?.l || q.type;
      const idPill = document.createElement('span'); idPill.className = 'q-id-pill'; idPill.textContent = q.id; idPill.title = 'Question ID: ' + q.id;
      const collapseBtn = document.createElement('button'); collapseBtn.type = 'button'; collapseBtn.className = 'q-collapse-btn';
      collapseBtn.title = _collapsedQids.has(q.id) ? 'Expand this question' : 'Collapse this question';
      collapseBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>';
      collapseBtn.addEventListener('click', (e: any) => {
        e.stopPropagation();
        if (_collapsedQids.has(q.id)) { _collapsedQids.delete(q.id); card.classList.remove('collapsed'); collapseBtn.title = 'Collapse this question'; }
        else { _collapsedQids.add(q.id); card.classList.add('collapsed'); collapseBtn.title = 'Expand this question'; }
        _updateCollapseAllBtn();
      });
      hdr.append(drag, num, idPill, labelInp, badge, collapseBtn);
      const frow = document.createElement('div'); frow.className = 'q-fields';
      const typeF = document.createElement('div'); typeF.className = 'q-field';
      const typeLbl = document.createElement('div'); typeLbl.className = 'q-field-lbl'; typeLbl.textContent = 'Question Type';
      const typeSel = document.createElement('select');
      Object.entries(TC).forEach(([t, c]: any) => { const o = document.createElement('option'); o.value = t; o.textContent = c.l; if (t === q.type) o.selected = true; typeSel.appendChild(o); });
      typeSel.addEventListener('change', () => { q.type = typeSel.value; badge.textContent = TC[q.type]?.l || q.type; q.meta = initMeta(q.type); cfgWrap.innerHTML = ''; buildCfg(q, cfgWrap); syncJson(); rebuildGotos(); });
      typeF.append(typeLbl, typeSel);
      const reqF = document.createElement('div'); reqF.className = 'q-field';
      const reqRow = document.createElement('div'); reqRow.className = 'q-req-row';
      const reqSpan = document.createElement('span'); reqSpan.className = 'q-req-lbl'; reqSpan.textContent = 'Required field';
      const togLabel = document.createElement('label'); togLabel.className = 'q-toggle';
      const togInp = document.createElement('input'); togInp.type = 'checkbox'; togInp.checked = q.required;
      togInp.addEventListener('change', () => { q.required = togInp.checked; syncJson(); });
      const togSlider = document.createElement('span'); togSlider.className = 'q-toggle-slider';
      togLabel.append(togInp, togSlider); reqRow.append(reqSpan, togLabel); reqF.appendChild(reqRow);
      frow.append(typeF, reqF);
      const cfgWrap = document.createElement('div'); buildCfg(q, cfgWrap);
      const brToggle = document.createElement('div'); brToggle.className = 'q-branch-toggle';
      brToggle.innerHTML = '<svg class="chev" width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg><span>Branching / Skip logic</span>';
      const brBody = document.createElement('div'); brBody.className = 'q-branch-body';
      const brRow = document.createElement('div'); brRow.className = 'branch-row';
      const brLbl = document.createElement('span'); brLbl.className = 'branch-lbl'; brLbl.textContent = 'Default next:';
      const defNextSel = document.createElement('select'); defNextSel.setAttribute('data-default-next', '1'); defNextSel.style.flex = '1'; defNextSel.style.fontSize = '12px';
      defNextSel.addEventListener('change', () => { q.meta.branch.defaultNext = defNextSel.value; syncJson(); rebuildGotos(); });
      brRow.append(brLbl, defNextSel); brBody.appendChild(brRow);
      brToggle.addEventListener('click', () => { brToggle.classList.toggle('open'); brBody.classList.toggle('open'); });
      populateGoto(defNextSel, q, q.meta?.branch?.defaultNext || '');
      inner.append(hdr, frow, cfgWrap, brToggle, brBody);
      const actions = document.createElement('div'); actions.className = 'q-actions';
      const tLabel = document.createElement('span'); tLabel.className = 'txt-xs muted2'; tLabel.textContent = TC[q.type]?.l || q.type;
      const sp = document.createElement('span'); sp.className = 'spacer';
      const dupBtn = document.createElement('button'); dupBtn.className = 'btn sm ghost'; dupBtn.type = 'button'; dupBtn.textContent = 'Duplicate';
      const delBtn = document.createElement('button'); delBtn.className = 'btn sm danger'; delBtn.type = 'button'; delBtn.textContent = 'Delete';
      dupBtn.onclick = () => { const cl = JSON.parse(JSON.stringify(q)); cl.id = uid(); state.questions.splice(idx + 1, 0, cl); state.questions.forEach((qq: any, i: number) => qq.order = i); renderQuestions(); syncJson(); rebuildGotos(); renderSections(); toast('Duplicated', 'ok'); };
      delBtn.onclick = () => { const delId = q.id; state.questions.splice(idx, 1); state.questions.forEach((qq: any) => { const br = qq.meta?.branch; if (!br) return; if (br.defaultNext === delId) br.defaultNext = ''; Object.keys(br.byValue || {}).forEach(k => { if (br.byValue[k] === delId) br.byValue[k] = ''; }); }); state.sections.forEach((s: any) => s.questionIds = (s.questionIds || []).filter((id: string) => id !== delId)); state.questions.forEach((qq: any, i: number) => qq.order = i); renderQuestions(); renderSections(); syncJson(); rebuildGotos(); toast('Deleted', 'info'); };
      actions.append(tLabel, sp, dupBtn, delBtn);
      card.append(inner, actions); drop.appendChild(card);
    });
    _updateCollapseAllBtn();
  }

  // ── Copy / Paste selected question cards (document-level keydown) ──
  const _keydownHandler = (e: any) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod || e.shiftKey || e.altKey) return;
    const k = e.key.toLowerCase();
    if (k !== 'c' && k !== 'v') return;
    const ae: any = document.activeElement;
    if (ae) {
      const tag = ae.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return;
    }
    const vb = $('view-builder');
    if (!vb || vb.classList.contains('hide')) return;
    if (k === 'c') {
      if (!_selectedQids.size) return;
      const sel = state.questions.filter((qq: any) => _selectedQids.has(qq.id));
      _clipboardQs = JSON.parse(JSON.stringify(sel));
      e.preventDefault();
      toast('Copied ' + _clipboardQs.length + ' question' + (_clipboardQs.length > 1 ? 's' : ''), 'ok');
      return;
    }
    if (!_clipboardQs.length) return;
    e.preventDefault();
    let insertAt = state.questions.length;
    if (_anchorQid) {
      const i = state.questions.findIndex((qq: any) => qq.id === _anchorQid);
      if (i >= 0) insertAt = i + 1;
    }
    const fresh = _clipboardQs.map((orig: any) => { const c = JSON.parse(JSON.stringify(orig)); c.id = uid(); return c; });
    state.questions.splice(insertAt, 0, ...fresh);
    state.questions.forEach((qq: any, i: number) => qq.order = i);
    _selectedQids = new Set(fresh.map((qq: any) => qq.id));
    _anchorQid = fresh[fresh.length - 1].id;
    renderQuestions(); syncJson(); rebuildGotos(); renderSections();
    toast('Pasted ' + fresh.length + ' question' + (fresh.length > 1 ? 's' : ''), 'ok');
  };
  document.addEventListener('keydown', _keydownHandler);

  // ═══ CONFIG PANELS ═════════════════════════════════════════
  function buildCfg(q: any, wrap: any) {
    if (HC(q.type)) {
      buildOptsEditor(q, wrap);
      if (q.type === 'CHECKBOX_OTHER') {
        if (q.meta.mode == null) q.meta.mode = 'multi';
        if (q.meta.otherLabel == null) q.meta.otherLabel = 'Other';
        const ow = document.createElement('div'); ow.style.cssText = 'display:grid;gap:10px;margin-top:10px;padding:10px 12px;border:1px solid var(--border);border-radius:var(--r-md);background:var(--s1)';
        const mRow = document.createElement('div'); mRow.style.cssText = 'display:flex;align-items:center;gap:14px;flex-wrap:wrap';
        const mLbl = document.createElement('div'); mLbl.className = 'q-field-lbl'; mLbl.textContent = 'Selection mode'; mLbl.style.margin = '0';
        const grp = document.createElement('div'); grp.style.cssText = 'display:flex;gap:14px;flex-wrap:wrap';
        [['multi', 'Multi-select (checkboxes)'], ['single', 'Single-select (radio)']].forEach(([m, lbl]) => {
          const w = document.createElement('label'); w.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;color:var(--txt)';
          const rb = document.createElement('input'); rb.type = 'radio'; rb.name = q.id + '_co_mode'; rb.value = m; if (q.meta.mode === m) rb.checked = true;
          rb.addEventListener('change', () => {
            if (!rb.checked) return;
            q.meta.mode = m;
            if (m === 'multi' && q.meta.branch) q.meta.branch.byValue = {};
            syncJson(); renderQuestions(); rebuildGotos();
          });
          const sp = document.createElement('span'); sp.textContent = lbl;
          w.append(rb, sp); grp.appendChild(w);
        });
        mRow.append(mLbl, grp); ow.appendChild(mRow);
        const oRow = document.createElement('div'); oRow.style.cssText = 'display:flex;align-items:center;gap:10px';
        const oLbl = document.createElement('div'); oLbl.className = 'q-field-lbl'; oLbl.textContent = '"Other" label'; oLbl.style.margin = '0'; oLbl.style.whiteSpace = 'nowrap';
        const oInp = document.createElement('input'); oInp.type = 'text'; oInp.value = q.meta.otherLabel || 'Other'; oInp.placeholder = 'Other'; oInp.style.flex = '1';
        oInp.addEventListener('input', () => { q.meta.otherLabel = oInp.value; syncJson(); });
        oRow.append(oLbl, oInp); ow.appendChild(oRow);
        wrap.appendChild(ow);
      }
      if (q.type === 'RANKING') {
        const f = document.createElement('div'); f.className = 'q-field'; f.style.marginTop = '8px';
        const l = document.createElement('div'); l.className = 'q-field-lbl'; l.textContent = 'Number of options to select before ranking';
        const inp = document.createElement('input'); inp.type = 'number'; inp.min = '0'; inp.max = '50'; inp.value = q.meta.rankSelectCount || 0;
        inp.style.cssText = 'width:80px';
        inp.addEventListener('input', () => { q.meta.rankSelectCount = +inp.value || 0; syncJson(); });
        const hint = document.createElement('p'); hint.className = 'txt-xs muted2'; hint.style.cssText = 'margin-top:4px;line-height:1.4';
        hint.textContent = 'Set how many options the respondent must pick before ranking them. Set to 0 to rank all options directly.';
        f.append(l, inp, hint); wrap.appendChild(f);
      }
    }
    else if (HG(q.type)) { buildGridEditor(q, wrap); }
    else if (HS(q.type)) { buildScaleEditor(q, wrap); }
    else if (q.type === 'RATING') {
      const f = document.createElement('div'); f.className = 'q-field'; f.style.marginTop = '8px';
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.textContent = 'Max value';
      const inp = document.createElement('input'); inp.type = 'number'; inp.min = '2'; inp.max = '20'; inp.value = q.meta.max || 10;
      inp.addEventListener('input', () => { q.meta.max = +inp.value || 10; syncJson(); });
      f.append(l, inp); wrap.appendChild(f);
    } else if (HCN(q.type)) {
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.style.margin = '8px 0 4px';
      l.textContent = q.type === 'NOTE' ? 'Note content' : 'Statement to acknowledge';
      const ta = document.createElement('textarea'); ta.value = q.meta.content || ''; ta.rows = 3; ta.placeholder = 'Enter text…';
      ta.addEventListener('input', () => { q.meta.content = ta.value; syncJson(); });
      wrap.append(l, ta);
    } else if (HF(q.type)) {
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.style.margin = '8px 0 4px'; l.textContent = 'Formula';
      const inp = document.createElement('input'); inp.type = 'text'; inp.value = q.meta.formula || ''; inp.placeholder = 'e.g. ${field1} + ${field2}';
      inp.addEventListener('input', () => { q.meta.formula = inp.value; syncJson(); });
      wrap.append(l, inp);
    } else if (q.type === 'HIDDEN') {
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.style.margin = '8px 0 4px'; l.textContent = 'Default value';
      const inp = document.createElement('input'); inp.type = 'text'; inp.value = q.meta.defaultValue || ''; inp.placeholder = 'Default value…';
      inp.addEventListener('input', () => { q.meta.defaultValue = inp.value; syncJson(); });
      wrap.append(l, inp);
    } else if (q.type === 'LOCATION') {
      const note = document.createElement('p'); note.className = 'txt-xs muted2'; note.style.cssText = 'margin-top:8px;line-height:1.5';
      note.textContent = 'Captures a point (lat/lng) and optionally a multi-point path or area — all in one unified input.';
      wrap.appendChild(note);
    } else if (['PHOTO', 'AUDIO', 'VIDEO', 'FILE_UPLOAD'].includes(q.type)) {
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.style.margin = '8px 0 4px'; l.textContent = 'Save attachments to folder';
      const inp = document.createElement('input'); inp.type = 'text'; inp.value = q.meta.folder || ''; inp.placeholder = 'e.g. Patient Records';
      inp.addEventListener('input', () => { q.meta.folder = inp.value; syncJson(); });
      const note = document.createElement('p'); note.className = 'txt-xs muted2'; note.style.marginTop = '4px';
      note.textContent = 'Files auto-download to your downloads folder with this prefix on submission.';
      wrap.append(l, inp, note);
      if (q.meta.maxFileSizeMB == null) q.meta.maxFileSizeMB = 0;
      const f2 = document.createElement('div'); f2.className = 'q-field'; f2.style.marginTop = '10px';
      const l2 = document.createElement('div'); l2.className = 'q-field-lbl'; l2.textContent = 'Max file size (MB)';
      const inp2 = document.createElement('input'); inp2.type = 'number'; inp2.min = '0'; inp2.max = '500'; inp2.step = '0.5'; inp2.value = q.meta.maxFileSizeMB || 0;
      inp2.style.cssText = 'width:100px';
      inp2.addEventListener('input', () => { q.meta.maxFileSizeMB = +inp2.value || 0; syncJson(); });
      const note2 = document.createElement('p'); note2.className = 'txt-xs muted2'; note2.style.cssText = 'margin-top:4px;line-height:1.4';
      note2.textContent = 'Maximum allowed file size in megabytes. Set to 0 for no limit.';
      f2.append(l2, inp2, note2); wrap.appendChild(f2);
    } else if (q.type === 'PHONE') {
      const f = document.createElement('div'); f.className = 'q-field'; f.style.marginTop = '8px';
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.textContent = 'Default country';
      const sel = document.createElement('select');
      COUNTRIES.forEach((c: any) => { const o = document.createElement('option'); o.value = c.iso; o.textContent = c.flag + '  ' + c.name + ' (+' + c.dial + ')'; if ((q.meta.defaultCountry || 'IN') === c.iso) o.selected = true; sel.appendChild(o); });
      sel.addEventListener('change', () => { q.meta.defaultCountry = sel.value; syncJson(); });
      const note = document.createElement('p'); note.className = 'txt-xs muted2'; note.style.marginTop = '4px';
      note.textContent = 'Respondents pick a country code (with flag) and the number is validated for that country’s format & length.';
      f.append(l, sel, note); wrap.appendChild(f);
    } else if (['SHORT_TEXT', 'LONG_TEXT', 'EMAIL'].includes(q.type)) {
      const row = document.createElement('div'); row.className = 'q-fields'; row.style.marginTop = '8px';
      const f1 = document.createElement('div'); f1.className = 'q-field';
      const l1 = document.createElement('div'); l1.className = 'q-field-lbl'; l1.textContent = 'Placeholder';
      const i1 = document.createElement('input'); i1.type = 'text'; i1.value = q.meta.placeholder || ''; i1.placeholder = 'Hint text…';
      i1.addEventListener('input', () => { q.meta.placeholder = i1.value; syncJson(); });
      f1.append(l1, i1);
      const f2 = document.createElement('div'); f2.className = 'q-field';
      const l2 = document.createElement('div'); l2.className = 'q-field-lbl'; l2.textContent = 'Regex validation';
      const i2 = document.createElement('input'); i2.type = 'text'; i2.value = q.meta.regex || ''; i2.placeholder = '.+@.+';
      i2.addEventListener('input', () => { q.meta.regex = i2.value; syncJson(); });
      f2.append(l2, i2); row.append(f1, f2); wrap.appendChild(row);
    }
  }

  // ── Options Editor ──────────────────────────────────────
  function buildOptsEditor(q: any, wrap: any) {
    if (!q.meta.choices) q.meta.choices = [];
    if (!q.meta.branch.byValue) q.meta.branch.byValue = {};
    const ow = document.createElement('div'); ow.className = 'q-options-wrap';
    const head = document.createElement('div'); head.className = 'q-options-head';
    const title = document.createElement('span'); title.className = 'q-options-title';
    title.textContent = q.type === 'RANKING' ? 'Items to Rank' : 'Options';
    const btns = document.createElement('div'); btns.className = 'row';
    const addBtn = document.createElement('button'); addBtn.className = 'btn sm'; addBtn.type = 'button'; addBtn.textContent = '+ Add option';
    const clearBtn = document.createElement('button'); clearBtn.className = 'btn sm ghost'; clearBtn.type = 'button'; clearBtn.textContent = 'Clear';
    btns.append(addBtn, clearBtn); head.append(title, btns);
    const list = document.createElement('div'); list.setAttribute('data-opts-list', '1');
    ow.append(head, list); wrap.appendChild(ow);
    function syncChoices() {
      const containers = [...list.querySelectorAll('[data-oc]')];
      const choices: any[] = [], byValue: any = {};
      containers.forEach((c: any) => {
        const v = c.querySelector('.opt-input')?.value?.trim();
        if (!v) return;
        const blockMsg = c.querySelector('.opt-block-inp')?.value || '';
        choices.push({ value: v, label: v, blockMsg });
        const gs = c.querySelector('[data-goto]');
        if (gs) byValue[v] = gs.value || '';
      });
      q.meta.choices = choices; q.meta.branch.byValue = byValue; syncJson();
    }
    function addOptRow(val = '', goto = '', blockMsg = '') {
      const container = document.createElement('div'); container.setAttribute('data-oc', '1');
      const row = document.createElement('div'); row.className = 'opt-row';
      const bullet = document.createElement('span'); bullet.className = 'opt-bullet';
      const _coSingle = (q.type === 'CHECKBOX_OTHER' && (q.meta?.mode || 'multi') === 'single');
      bullet.textContent = (q.type === 'MULTI_SELECT' || (q.type === 'CHECKBOX_OTHER' && !_coSingle)) ? '☐' : (q.type === 'RANKING' ? '☰' : '○');
      const inp = document.createElement('input'); inp.type = 'text'; inp.className = 'opt-input';
      inp.placeholder = 'Option label'; inp.value = val;
      const delBtn = document.createElement('button'); delBtn.type = 'button'; delBtn.className = 'btn sm ghost icon'; delBtn.textContent = '✕';
      const allowBranch = q.type !== 'MULTI_SELECT' && !(q.type === 'CHECKBOX_OTHER' && !_coSingle);
      if (allowBranch) {
        const brBtn = document.createElement('button'); brBtn.type = 'button'; brBtn.className = 'btn sm ghost icon'; brBtn.title = "Choose this option's destination"; brBtn.textContent = '↳';
        row.append(bullet, inp, brBtn, delBtn);
        const brSub = document.createElement('div'); brSub.className = 'opt-branch-sub';
        const bLbl = document.createElement('span'); bLbl.className = 'tiny-lbl'; bLbl.textContent = 'then go to:';
        const gotoSel = document.createElement('select'); gotoSel.setAttribute('data-goto', '1');
        brSub.append(bLbl, gotoSel);
        const blockWrap = document.createElement('div'); blockWrap.className = 'opt-block-wrap' + (goto === 'block' ? ' show' : '');
        const blockLbl = document.createElement('div'); blockLbl.className = 'opt-block-lbl'; blockLbl.textContent = '⛔ Message shown to respondent:';
        const blockInp = document.createElement('input'); blockInp.type = 'text'; blockInp.className = 'opt-block-inp';
        blockInp.placeholder = 'e.g. Sorry, you are not eligible to proceed.';
        blockInp.value = blockMsg;
        blockWrap.append(blockLbl, blockInp);
        container.append(row, brSub, blockWrap); list.appendChild(container);
        populateGoto(gotoSel, q, goto);
        gotoSel.addEventListener('change', () => { blockWrap.classList.toggle('show', gotoSel.value === 'block'); syncChoices(); rebuildGotos(); });
        brBtn.addEventListener('click', () => brSub.classList.toggle('show'));
        blockInp.addEventListener('input', syncChoices);
      } else {
        row.append(bullet, inp, delBtn);
        container.append(row); list.appendChild(container);
      }
      inp.addEventListener('input', syncChoices);
      delBtn.addEventListener('click', () => { container.remove(); syncChoices(); rebuildGotos(); });
    }
    (q.meta.choices || []).forEach((c: any) => addOptRow(c.label, q.meta.branch?.byValue?.[c.label] || '', c.blockMsg || ''));
    addBtn.addEventListener('click', () => addOptRow(''));
    clearBtn.addEventListener('click', () => { list.innerHTML = ''; q.meta.choices = []; q.meta.branch.byValue = {}; syncJson(); rebuildGotos(); });
  }

  // ── Grid Editor ─────────────────────────────────────────
  function buildGridEditor(q: any, wrap: any) {
    if (!q.meta.rows) q.meta.rows = [];
    if (!q.meta.cols) q.meta.cols = [];
    const title = document.createElement('div'); title.className = 'q-options-title'; title.style.margin = '8px 0 4px'; title.textContent = 'Grid Configuration';
    const ged = document.createElement('div'); ged.className = 'grid-ed';
    function mkList(lbl: string, arr: any[]) {
      const col = document.createElement('div');
      const h = document.createElement('h5'); h.textContent = lbl;
      const list = document.createElement('div');
      const addB = document.createElement('button'); addB.type = 'button'; addB.className = 'btn sm'; addB.style.marginTop = '6px'; addB.textContent = '+ Add';
      function syncArr() { arr.length = 0; list.querySelectorAll('.opt-input').forEach((inp: any) => { const v = inp.value.trim(); if (v) arr.push(v); }); syncJson(); }
      function addRow(val = '') {
        const row = document.createElement('div'); row.className = 'opt-row';
        const inp = document.createElement('input'); inp.type = 'text'; inp.className = 'opt-input'; inp.placeholder = lbl.slice(0, -1) + ' label'; inp.value = val;
        const del = document.createElement('button'); del.type = 'button'; del.className = 'btn sm ghost icon'; del.textContent = '✕';
        inp.addEventListener('input', syncArr); del.addEventListener('click', () => { row.remove(); syncArr(); });
        row.append(inp, del); list.appendChild(row);
      }
      arr.forEach((v: any) => addRow(v)); addB.addEventListener('click', () => addRow(''));
      col.append(h, list, addB); return col;
    }
    ged.append(mkList('Rows', q.meta.rows), mkList('Columns', q.meta.cols));
    wrap.append(title, ged);
  }

  // ── Scale Editor ────────────────────────────────────────
  function buildScaleEditor(q: any, wrap: any) {
    const title = document.createElement('div'); title.className = 'q-options-title'; title.style.margin = '8px 0 4px'; title.textContent = 'Scale Configuration';
    const cfg = document.createElement('div'); cfg.className = 'scale-cfg';
    function mkF(lbl: string, key: string, type = 'number', ph = '') {
      const f = document.createElement('div'); f.className = 'q-field';
      const l = document.createElement('div'); l.className = 'q-field-lbl'; l.textContent = lbl;
      const inp = document.createElement('input'); inp.type = type; inp.value = q.meta[key] ?? ''; inp.placeholder = ph;
      inp.addEventListener('input', () => { q.meta[key] = type === 'number' ? +inp.value : inp.value; syncJson(); });
      f.append(l, inp); return f;
    }
    cfg.append(mkF('Min', 'min'), mkF('Max', 'max'), mkF('Step', 'step'));
    const lRow = document.createElement('div'); lRow.style.cssText = 'grid-column:1/-1;display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:4px';
    lRow.append(mkF('Min Label', 'minLabel', 'text', 'Low'), mkF('Max Label', 'maxLabel', 'text', 'High'));
    wrap.append(title, cfg, lRow);
  }

  // ═══ GOTO SELECTS ════════════════════════════════════════
  function buildTargetOpts(skipId: any) {
    const opts: any[] = [{ value: '', label: '(Next in order)' }];
    opts.push(...state.questions.filter((q: any) => q.id !== skipId).map((q: any) => ({ value: q.id, label: 'Q: ' + q.label + ' (' + q.id + ')' })));
    opts.push(...state.sections.map((s: any) => ({ value: '@' + s.id, label: 'Section: ' + s.title })));
    opts.push({ value: 'end', label: 'End of form' });
    opts.push({ value: 'block', label: '⛔ Block — show message' });
    return opts;
  }
  function sectionUsageMap() {
    const map: any = {};
    state.questions.forEach((q: any) => {
      const br = q.meta?.branch; if (!br) return;
      const targets = [br.defaultNext, ...Object.values(br.byValue || {})];
      targets.forEach((t: any) => {
        if (typeof t === 'string' && t.startsWith('@') && !map[t]) map[t] = { qid: q.id, label: q.label || q.id };
      });
    });
    return map;
  }
  function populateGoto(sel: any, currentQ: any, selected?: any) {
    const prev = sel.value; sel.innerHTML = '';
    const selVal = (selected !== undefined ? selected : prev);
    const usage = sectionUsageMap();
    const noNav = currentQ?.type === 'MULTI_SELECT' || (currentQ?.type === 'CHECKBOX_OTHER' && (currentQ.meta?.mode || 'multi') !== 'single');
    buildTargetOpts(currentQ?.id).forEach((o: any) => {
      if (noNav && o.value !== '' && o.value !== 'end' && o.value !== 'block') return;
      const opt = document.createElement('option'); opt.value = o.value; opt.textContent = o.label;
      if (o.value && o.value.startsWith('@') && usage[o.value] && o.value !== selVal) {
        opt.disabled = true;
        opt.textContent = o.label + ' (Used in ' + usage[o.value].label + ')';
      }
      if (o.value === selVal) opt.selected = true;
      sel.appendChild(opt);
    });
  }
  function rebuildGotos() {
    $$('select[data-default-next],select[data-goto]').forEach((sel: any) => {
      const card = sel.closest('.q-card'); if (!card) return;
      const q = state.questions.find((x: any) => x.id === card.dataset.qid); if (!q) return;
      const saved = sel.value || (sel.hasAttribute('data-default-next') ? q.meta?.branch?.defaultNext || '' : '');
      populateGoto(sel, q, saved);
    });
    $$('select[data-sec-next]').forEach((sel: any) => ctx.populateSecNextSel(sel, sel.value));
  }

  // ── Canvas title/description inputs ──
  const titleEl = $('form-title'), descEl = $('form-desc');
  titleEl.addEventListener('input', () => { state.title = titleEl.value || 'Untitled Form'; syncJson(); });
  descEl.addEventListener('input', () => { state.description = descEl.value; syncJson(); });

  // ── Clear button ──
  $('clear').onclick = () => { state.questions = []; state.sections = []; ctx.resetUidSeq?.(); _collapsedQids.clear(); renderQuestions(); renderSections(); syncJson(); rebuildGotos(); toast('Cleared', 'info'); };

  // ── Collapse all / Expand all toggle ──
  $('collapse-all').onclick = () => {
    if (!state.questions.length) return;
    const allCollapsed = state.questions.every((q: any) => _collapsedQids.has(q.id));
    if (allCollapsed) { _collapsedQids.clear(); }
    else { state.questions.forEach((q: any) => _collapsedQids.add(q.id)); }
    $$('.q-card').forEach((c: any) => { c.classList.toggle('collapsed', _collapsedQids.has(c.dataset.qid)); });
    _updateCollapseAllBtn();
  };

  function teardownBuilderRender() {
    document.removeEventListener('keydown', _keydownHandler);
  }

  // ── Wire into Ctx ──
  ctx.renderLogoPicker = renderLogoPicker;
  ctx.removeLogo = removeLogo;
  ctx.restoreCoverMedia = restoreCoverMedia;
  ctx.addQ = addQ;
  ctx.renderQuestions = renderQuestions;
  ctx.buildCfg = buildCfg;
  ctx.buildTargetOpts = buildTargetOpts;
  ctx.sectionUsageMap = sectionUsageMap;
  ctx.populateGoto = populateGoto;
  ctx.rebuildGotos = rebuildGotos;
  ctx.updateBadge = updateBadge;
  ctx.toggleEmpty = toggleEmpty;
  ctx.teardownBuilderRender = teardownBuilderRender;

  // Initial palette render (was an IIFE at load time in the legacy script).
  renderPalette();
}
